import React, { useRef, useEffect, forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

const StyledCellContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  outline: ${(props) => (props.isFocused ? '2px solid var(--color-primary)' : 'none')};
  outline-offset: -2px;
  background-color: ${(props) => (props.isFocused ? 'rgba(59, 130, 246, 0.1)' : 'transparent')};
  transition: all 0.15s ease;
  
  &:focus-within {
    outline: 2px solid var(--color-primary);
    outline-offset: -2px;
    background-color: rgba(59, 130, 246, 0.1);
  }
`;

const CellContainer = forwardRef(({ isFocused, children, ...props }, ref) => (
  <StyledCellContainer ref={ref} isFocused={isFocused} {...props}>
    {children}
  </StyledCellContainer>
));

CellContainer.propTypes = {
  isFocused: PropTypes.bool,
  children: PropTypes.node,
};

CellContainer.defaultProps = {
  isFocused: false,
  children: null,
};

const NavigationWrapper = ({
  children,
  rowIndex,
  colIndex,
  navigation,
  ...props
}) => {
  const cellRef = useRef(null);

  const isNavigationReady = navigation.isTableReady && navigation.isInitialized;

  const isFocused = isNavigationReady &&
    navigation.focusedCell.rowIndex === rowIndex &&
    navigation.focusedCell.colIndex === colIndex;

  useEffect(() => {
    if (!isNavigationReady) {
      navigation.registerCellRef(rowIndex, colIndex, null);

      return;
    }

    if (cellRef.current) {
      navigation.registerCellRef(rowIndex, colIndex, cellRef.current);
    }

    // eslint-disable-next-line consistent-return
    return function cleanup() {
      navigation.registerCellRef(rowIndex, colIndex, null);
    };
  }, [rowIndex, colIndex, navigation, isNavigationReady]);

  useEffect(() => {
    if (!isNavigationReady || !cellRef.current) return undefined;

    const cellElement = cellRef.current;

    if (cellElement && cellElement.nodeType === 1) {
      const inputs = cellElement.querySelectorAll('input, textarea');

      const handleKeyDown = (event) => {
        navigation.handleCellKeyDown(event);
      };

      inputs.forEach((input) => {
        input.addEventListener('keydown', handleKeyDown);
      });

      return () => {
        inputs.forEach((input) => {
          input.removeEventListener('keydown', handleKeyDown);
        });
      };
    }

    return undefined;
  }, [navigation.handleCellKeyDown, isNavigationReady]);

  return (
    <CellContainer
      ref={cellRef}
      isFocused={isFocused}
      {...props}
    >
      {children}
    </CellContainer>
  );
};

NavigationWrapper.propTypes = {
  children: PropTypes.node,
  rowIndex: PropTypes.number.isRequired,
  colIndex: PropTypes.number.isRequired,
  navigation: PropTypes.shape({
    isTableReady: PropTypes.bool,
    isInitialized: PropTypes.bool,
    focusedCell: PropTypes.shape({
      rowIndex: PropTypes.number,
      colIndex: PropTypes.number,
    }),
    registerCellRef: PropTypes.func,
    handleCellKeyDown: PropTypes.func,
  }).isRequired,
};

NavigationWrapper.defaultProps = {
  children: null,
};

export default NavigationWrapper;
